import { useOktaAuth } from '@okta/okta-react';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Input,
  Alert,
  Checkbox,
  VStack,
  HStack,
  Center,
  Text,
  Switch
} from '@chakra-ui/react';
import PulseLoader from "react-spinners/PulseLoader";
import { useTranslation } from 'react-i18next';
import './ManagerIdentityVerification.css';

const ManagerIdentityVerification = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Extract sessionid from URL parameters
  const { authState, oktaAuth } = useOktaAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [buttonClickable, setButtonClickable] = useState(true);
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isRemoteDevice, setIsRemoteDevice] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [certificationAccepted, setCertificationAccepted] = useState(false);
  const [hideForm, setHideForm] = useState(false);
  const [localeSet, setLocaleSet] = useState(false); // Prevent infinite loop of locale changes

  useEffect(() => {
    // Extract sessionid and user email from URL parameters
    const urlParams = new URLSearchParams(location.search);
    const sessionIdParam = urlParams.get('token');
    const userEmailParam = urlParams.get('user');
    const locale = urlParams.get('locale');

    if (locale && !localeSet) {
      console.log('Manager Identity Verification - Locale:', locale);
      i18n.changeLanguage(locale);
      setLocaleSet(true);
    }

    if (userEmailParam) {
      setUserEmail(userEmailParam);
    } else {
      setHideForm(true);
      setErrorMessage(t('User email is required for manager sessions.'));
    }

    if (sessionIdParam) {
      setSessionId(sessionIdParam);
    } else {
      setHideForm(true);
      setErrorMessage(t('Session ID is required for manager sessions.'));
    }
  }, [location.search, t]);

  useEffect(() => {
    if (!authState || !authState.isAuthenticated) {
      // When user isn't authenticated, forget any user info
      setUserInfo(null);
      setSessionId(null);
    } else {
      oktaAuth.getUser().then((info) => {
        setUserInfo(info);
      });
      console.log('Manager Identity Verification - Session ID:', sessionId);
    }
  }, [authState, oktaAuth, sessionId]);

  // Function to handle the identity verification API call
  function handleVerification() {
    // Validate the 6-digit code
    const codeString = verificationCode.join('');
    if (codeString.length !== 6 || !/^\d{6}$/.test(codeString)) {
      setErrorMessage(t('Please enter a valid 6-digit verification code'));
      return;
    }

    // Validate certification acceptance
    if (!certificationAccepted) {
      setErrorMessage(t('manager_identity_verification_certify_identity','You must certify that you have verified the user\'s identity to proceed'));
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setButtonClickable(false);

    // Call the verification API - we need to determine the API endpoint
    // For now, using a generic endpoint that should handle manager verification
    const apiEndpoint = import.meta.env.VITE_MFA_RESET_API;
    
    fetch(apiEndpoint, {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        auth_code: codeString,
        remote_user: isRemoteDevice
      }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + authState.accessToken.accessToken
      }
    })
      .then((res) => {
        if (!res.ok) {
          // Handle HTTP error status codes
          return res.json().then(errorData => {
            throw new Error(JSON.stringify(errorData));
          });
        }
        return res.json();
      })
      .then((data) => {
        setLoading(false);
        
          // Success case
          setVerificationComplete(true);

      })
      .catch(error => {
        setLoading(false);
        setButtonClickable(true);
        
        try {
          // Try to parse the error as JSON (from our HTTP error handling)
          const errorData = JSON.parse(error.message);
          
          if (errorData.error) {
            const errorCode = errorData.error.code;
            const errorMessage = errorData.error.message;
            
            // Handle specific error codes
            let userMessage;
            switch (errorCode) {
              case 'SESSION_NOT_FOUND':
                userMessage = t('manager_verification_session_not_found', 'The verification session has expired or is invalid. Please request a new verification code.');
                setHideForm(true);
                break;
              case 'INVALID_CODE':
                userMessage = t('manager_verification_invalid_code', 'The verification code is incorrect. Please check the code and try again.');
                break;
              case 'CODE_EXPIRED':
                userMessage = t('manager_verification_code_expired', 'The verification code has expired. Please request a new verification code.');
                setHideForm(true);
                break;
              case 'TOO_MANY_ATTEMPTS':
                userMessage = t('manager_verification_too_many_attempts', 'Too many verification attempts. Please wait one hour before trying again.');
                setHideForm(true);
                break;
              case 'USER_NOT_FOUND':
                userMessage = t('manager_verification_user_not_found', 'The user associated with this session could not be found.');
                setHideForm(true);
                break;
              case 'UNAUTHORIZED':
                userMessage = t('manager_verification_unauthorized', 'You are not authorized to perform this verification.');
                setHideForm(true);
                break;
              default:
                userMessage = errorMessage || t('An error occurred during verification');
            }
            
            setErrorMessage(userMessage);
            
            // Log the full error for debugging
            console.error('Manager Identity Verification Network Error:', {
              code: errorCode,
              message: errorMessage,
              details: errorData.error.details,
              timestamp: errorData.error.timestamp
            });
            
          } else {
            // Fallback for other JSON error formats
            setErrorMessage(errorData.message || t('An error occurred during verification'));
          }
        } catch (parseError) {
          // Handle network errors or other non-JSON errors
          if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
            setErrorMessage(t('manager_verification_network_error', 'Network error. Please check your connection and try again.'));
          } else {
            setErrorMessage(error.message || t('An error occurred during verification'));
          }
          
          console.error('Manager Identity Verification Error:', error);
        }
      });
  }

  // Handle input change for individual OTP boxes
  const handleCodeChange = (index, value) => {
    // Only allow single digit
    const digit = value.replace(/\D/g, '').slice(-1);
    
    const newCode = [...verificationCode];
    newCode[index] = digit;
    setVerificationCode(newCode);
    setErrorMessage(''); // Clear error when user types
    
    // Auto-focus next input if digit entered
    if (digit && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  // Handle key press for navigation and submission
  const handleKeyPress = (e, index) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    } else if (e.key === 'Enter' && verificationCode.join('').length === 6 && buttonClickable) {
      handleVerification();
    }
  };

  // Handle paste functionality
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = ['', '', '', '', '', ''];
    
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    
    setVerificationCode(newCode);
    setErrorMessage('');
    
    // Focus the next empty input or the last one
    const nextEmptyIndex = Math.min(pastedData.length, 5);
    const nextInput = document.getElementById(`otp-${nextEmptyIndex}`);
    if (nextInput) {
      nextInput.focus();
    }
  };

  if (!authState) {
    oktaAuth.signInWithRedirect({ originalUri: '/' });
    return (
      <div>Loading...</div>
    );
  }

  if (!sessionId && !errorMessage) {
    return (
      <Box>{t('Loading')}...</Box>
    );
  }

  if (!sessionId && errorMessage) {
    return (
      <Box>{t('error')}: {errorMessage}</Box>
    );
  }

  return (
    <Box id="manager-identity-verification">
      {loading && (
        <Box className="sweet-loading">
          <Box className="centered">
            <PulseLoader
              loading={loading}
              color="#dadada"
              size={30}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          </Box>
        </Box>
      )}
      
      <Box>
        {authState.isAuthenticated && !userInfo && (
          <Box>{t('Loading user information')}...</Box>
        )}

        {hideForm && errorMessage && (
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>{t('Verification Error')}</Alert.Title>
              <Alert.Description>{errorMessage}</Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}
            

        {authState.isAuthenticated && userInfo && !verificationComplete && !hideForm && (
          <Center>
            <VStack spacing={6} width="100%">
              <Heading as="h1" size="2xl">{t('Manager Identity Verification')}</Heading>
              <Box p={8} borderWidth="1px" borderRadius="lg" bg="white" shadow="sm" width="100%">
                <Text mb={6} className="verification-instructions">
                  {t('manager_identity_verification_generic_instructions', { email: userEmail })}
                </Text>
                
                <Box as="form" className="verification-form">
                  <Box mb={6}>
                    <HStack spacing={2} justify="center" className="otp-container">
                      {verificationCode.map((digit, index) => (
                        <Input
                          key={index}
                          id={`otp-${index}`}
                          value={digit}
                          onChange={(e) => handleCodeChange(index, e.target.value)}
                          onKeyDown={(e) => handleKeyPress(e, index)}
                          onPaste={index === 0 ? handlePaste : undefined}
                          maxLength={1}
                          className="otp-input"
                          autoFocus={index === 0}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          textAlign="center"
                          width="50px"
                          height="50px"
                          fontSize="xl"
                          fontWeight="bold"
                        />
                      ))}
                    </HStack>
                  </Box>
                  
                  <Box 
                    mb={6} 
                    p={4} 
                    borderWidth="1px" 
                    borderRadius="md"
                    bg={isRemoteDevice ? 'blue.50' : 'gray.50'}
                    className={isRemoteDevice ? 'remote-device-section-active' : 'remote-device-section-inactive'}
                  >
                    <Switch.Root checked={isRemoteDevice} onCheckedChange={(e) => setIsRemoteDevice(e.checked)} variant="raised">
                      <Switch.HiddenInput />
                      <Switch.Control />
                      <Switch.Label>{t('remote_device_checkbox_label')}</Switch.Label>
                    </Switch.Root>
                  </Box>
                  
                  <Box mb={6}>
                    <Box className="certification-section">
                      <Checkbox.Root checked={certificationAccepted} onCheckedChange={(e) => setCertificationAccepted(e.checked)}>
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>{t('manager_identity_verification_certification_checkbox_label', { email: userEmail })}</Checkbox.Label>
                      </Checkbox.Root>
                    </Box>
                  </Box>
                  
                  {errorMessage && (
                    <Alert.Root status="error" mb={4}>
                      <Alert.Indicator />
                      <Alert.Content>
                        <Alert.Title>{errorMessage}</Alert.Title>
                      </Alert.Content>
                    </Alert.Root>
                  )}
                  
                  <Center mt={6}>
                    <Button 
                      colorScheme="blue" 
                      size="lg" 
                      isDisabled={!buttonClickable || verificationCode.join('').length !== 6 || !certificationAccepted}
                      onClick={handleVerification}
                    >
                      {t('Verify Identity')}
                    </Button>
                  </Center>
                </Box>
              </Box>
            </VStack>
          </Center>
        )}

        {authState.isAuthenticated && userInfo && verificationComplete && (
          <Center>
            <VStack spacing={6} width="100%">
              <Heading as="h1" size="2xl" color="green.500" className="verification-success">
                {t('identity_verification_complete_title')}
              </Heading>
              <Box p={8} borderWidth="1px" borderRadius="lg" bg="white" shadow="sm" width="100%">
                <Text mb={4} fontSize="lg">
                  {t('identity_verification_complete_message')}
                </Text>
                <Text>
                  {t('Please close your web browser to end this session.')}
                </Text>
              </Box>
            </VStack>
          </Center>
        )}
      </Box>
    </Box>
  );
};

export default ManagerIdentityVerification;
